import type { ComputedRef } from 'vue'
import type { Store } from 'vuex'
import type { LoginUserState } from '@/store/modules/login-user'
import type { UserLoginInformationModel } from '@/api/system/model/userLoginInformationModel'

import { computed, watch, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'
import { actionTypes } from '@/store/modules/login-user'
import useStorage, { storageKeys } from './useStorage'
import registeredStore from '@/store'

export interface LoginUser {
  userInfo: ComputedRef<UserLoginInformationModel | null>
  setUserInfo: (userInfo: UserLoginInformationModel) => void
  corporatorsInfo: ComputedRef<UserLoginInformationModel[]>
  setCorporatorsInfo: (corporators: UserLoginInformationModel[]) => void
  changeCorporator: (corporatNumber: number | string) => void
}

export default function useLoginUser(): LoginUser {
  // setup() で使われているかをチェック。setup() 内ではない場合はモジュールとして store を読み込む
  const vueInstance = getCurrentInstance()
  const store: Store<any> = vueInstance ? useStore() : registeredStore
  const state: LoginUserState = store?.state.loginUser ?? {}

  const {
    loadUserLoginInformation,
    saveUserLoginInformation,
    saveDataToStorage,
    loadDataFromStorage,
  } = useStorage()

  /** @description ログインユーザー情報 */
  const userInfo = computed<UserLoginInformationModel | null>(
    () => state.userInfo ?? loadUserLoginInformation() ?? null
  )

  /**
   * @description ログインユーザー情報の設定方法
   * @param userInfo ログインユーザー情報
   */
  const setUserInfo = (userInfo: UserLoginInformationModel): void => {
    store.dispatch(actionTypes.SET_USER_INFO, userInfo)
  }

  /** @description ログインユーザーの登録済み会社での情報 */
  const corporatorsInfo = computed<UserLoginInformationModel[]>(
    () =>
      loadDataFromStorage<UserLoginInformationModel[]>(storageKeys.CORPORATORS_INFO) ??
      state.registeredCorporatorsInfo
  )

  // ストア中の登録済み会社情報に更新があればストレージ内のも更新
  watch<UserLoginInformationModel[]>(
    () => state.registeredCorporatorsInfo,

    (newValue): void =>
      saveDataToStorage<UserLoginInformationModel[]>(
        storageKeys.CORPORATORS_INFO,
        newValue
      )
  )

  /**
   * @description ログインユーザーの登録済み会社での情報を設定
   * @param corporators
   */
  const setCorporatorsInfo = (corporators: UserLoginInformationModel[]): void => {
    store.dispatch(actionTypes.SET_REGISTERED_CORPORATORS_INFO, corporators)
  }

  /**
   * @description 表示会社を切り替える
   * @param corporateNumber 法人番号
   */
  const changeCorporator = (corporateNumber: number | string): void => {
    const target: UserLoginInformationModel | undefined = corporatorsInfo.value.find(
      (item: UserLoginInformationModel): boolean =>
        item.corporate_number.toString() === corporateNumber.toString()
    )

    if (target) {
      saveUserLoginInformation(target)
      return
    }

    console.warn("Can't found corporator.")
  }

  return { userInfo, setUserInfo, corporatorsInfo, setCorporatorsInfo, changeCorporator }
}
